/**
 * How the focus presentation shows frame `id`, for every renderer that draws
 * focus (the canvas and the overlay above the wall).
 */
import { useMemo } from "react";
import type { FrameChromeState } from "@/components/shapes/shared/frame-renderer-context";
import { usePresentationStore } from "@/stores/presentation-store";

export function useFocusChromeState(id: string): FrameChromeState {
  const focused = usePresentationStore(
    (state) =>
      state.presentation?.variant === "focus" &&
      state.presentation.frameIds.includes(id),
  );
  const maximized = usePresentationStore((state) => {
    const presentation = state.presentation;
    return (
      presentation?.variant === "focus" &&
      presentation.focusWindow?.frameId === id &&
      presentation.focusWindow.mode === "maximized"
    );
  });
  const stackCollapsed = usePresentationStore((state) => {
    const layout = state.presentation?.customFocusLayout;
    return (
      layout?.kind === "conversation_companion_stack" &&
      layout.collapsedFrameIds.includes(id)
    );
  });
  const stackOverflowHidden = usePresentationStore((state) => {
    const layout = state.presentation?.customFocusLayout;
    return (
      layout?.kind === "conversation_companion_stack" &&
      layout.overflowHiddenFrameIds?.includes(id) === true
    );
  });
  const overflowTitleCount = usePresentationStore((state) => {
    const layout = state.presentation?.customFocusLayout;
    return layout?.kind === "conversation_companion_stack" &&
      layout.overflowTitleFrameId === id
      ? layout.overflowTitleCount
      : undefined;
  });
  return useMemo(
    () => ({
      focused,
      maximized,
      stackCollapsed,
      stackOverflowHidden,
      overflowTitleCount,
      preview: false,
    }),
    [
      focused,
      maximized,
      stackCollapsed,
      stackOverflowHidden,
      overflowTitleCount,
    ],
  );
}
